import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import { RecipeV2Detail, DietaryAttribute, DietaryAttributeStatus, DietaryCategory } from '../types';
import { UserContext } from '../../../components/shared/useUser';
import useRecipeV2Api from '../hooks/useRecipeV2Api';
import DietaryAttributeChips from './DietaryAttributeChips';

interface DietaryTabProps {
  recipe: RecipeV2Detail;
}

const lowerFirst = (value: string) => value.charAt(0).toLowerCase() + value.slice(1);

const DietaryTab = ({ recipe }: DietaryTabProps) => {
  const { t } = useTranslation('recipeV2');
  const { hasPermissionTo } = useContext(UserContext);
  const api = useRecipeV2Api();
  const canWrite = hasPermissionTo(['RecipeV2AllergenWrite']);
  const [attributes, setAttributes] = useState<DietaryAttribute[]>(recipe.dietaryAttributes ?? []);
  const [isLoading, setIsLoading] = useState(false);

  // Override dialog
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState<DietaryAttribute | null>(null);
  const [overrideStatus, setOverrideStatus] = useState<DietaryAttributeStatus>(DietaryAttributeStatus.FreeFrom);
  const [overrideReason, setOverrideReason] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    api.getDietaryAttributes(recipe.id)
      .then(setAttributes)
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [recipe.id, api]);

  const handleOpenOverride = (attr: DietaryAttribute) => {
    setSelectedAttribute(attr);
    setOverrideStatus(attr.status);
    setOverrideReason('');
    setOverrideDialogOpen(true);
  };

  const handleApplyOverride = async () => {
    if (!selectedAttribute) return;
    setIsSaving(true);
    try {
      await api.overrideDietaryAttribute(recipe.id, {
        attributeId: selectedAttribute.id,
        status: overrideStatus,
        reason: overrideReason,
      });
      setAttributes((prev) =>
        prev.map((a) => a.id === selectedAttribute.id ? { ...a, status: overrideStatus } : a),
      );
      setOverrideDialogOpen(false);
    } catch { /* noop */ }
    setIsSaving(false);
  };

  if (isLoading) {
    return (
      <Paper variant="outlined"
        sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('loading')}</Typography>
      </Paper>
    );
  }

  if (attributes.length === 0) {
    return (
      <Paper variant="outlined"
        sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('dietary.noAttributes')}</Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Grouped display */}
      <Paper variant="outlined"
        sx={{ p: 2 }}>
        <Typography variant="textXL"
          gutterBottom>{t('dietary.title')}</Typography>
        <DietaryAttributeChips
          attributes={attributes}
          showName
          groupByCategory
          size="medium"
          spacing={1}
        />
      </Paper>

      {/* Override management table */}
      {canWrite && (
        <Paper variant="outlined"
          sx={{ p: 2 }}>
          <Typography variant="textLG"
            fontWeight={600}
            gutterBottom>{t('dietary.manage')}</Typography>
          <Stack spacing={1}>
            {attributes.map((attr) => (
              <Stack
                key={attr.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 1, py: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
              >
                <Typography variant="textSM">{attr.name} ({t(`dietary.categories.${lowerFirst(attr.category)}`)})</Typography>
                <Stack direction="row"
                  spacing={1}
                  alignItems="center">
                  <Typography variant="textSM"
                    color="text.secondary">{t(`dietary.${lowerFirst(attr.status)}`)}</Typography>
                  <Button
                    size="md"
                    startIcon={<Edit />}
                    variant="secondary"
                    onClick={() => handleOpenOverride(attr)}
                  >
                    {t('dietary.override')}
                  </Button>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Override dialog */}
      <Dialog open={overrideDialogOpen}
        onClose={() => setOverrideDialogOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{t('dietary.overrideTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}
            sx={{ mt: 1 }}>
            {selectedAttribute && (
              <Typography variant="textSM"
                fontWeight={600}>{selectedAttribute.name}</Typography>
            )}
            <Select
              value={overrideStatus}
              onChange={(e) => setOverrideStatus(e.target.value as DietaryAttributeStatus)}
              size="small"
              fullWidth
            >
              <MenuItem value={DietaryAttributeStatus.Contains}>{t('dietary.contains')}</MenuItem>
              <MenuItem value={DietaryAttributeStatus.MayContain}>{t('dietary.mayContain')}</MenuItem>
              <MenuItem value={DietaryAttributeStatus.FreeFrom}>{t('dietary.freeFrom')}</MenuItem>
              <MenuItem value={DietaryAttributeStatus.Suitable}>{t('dietary.suitable')}</MenuItem>
              <MenuItem value={DietaryAttributeStatus.NotSuitable}>{t('dietary.notSuitable')}</MenuItem>
              <MenuItem value={DietaryAttributeStatus.Certified}>{t('dietary.certified')}</MenuItem>
            </Select>
            <TextField
              multiline
              rows={3}
              fullWidth
              label={t('dietary.overrideReason')}
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialogOpen(false)}
            variant="secondary">
            {t('actions.cancel')}
          </Button>
          <Button
            onClick={handleApplyOverride}
            variant="primary"
            disabled={isSaving || overrideReason.trim() === ''}
          >
            {t('dietary.overrideConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default DietaryTab;
